import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { X, ArrowRight, ArrowLeft, Bug, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/posthog";
import { supabase } from "@/integrations/supabase/client";
import { useTourProgress } from "@/hooks/useTourProgress";
import { useTourAnalytics } from "@/hooks/useTourAnalytics";

interface TourStep {
  id: string;
  title: string;
  description: string;
  route?: string;
  element?: string;
  position?: "top" | "bottom" | "left" | "right";
}

const tourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Vewly!",
    description: "Let's take a quick tour to help you get started with your personal media center.",
    route: "/",
  },
  {
    id: "dashboard",
    title: "Your Dashboard",
    description: "This is your central hub. View your media library, recent activity, and quick actions.",
    route: "/dashboard",
  },
  {
    id: "setup",
    title: "System Setup",
    description: "Configure your media libraries, hardware detection, and streaming settings here.",
    route: "/setup",
  },
  {
    id: "media",
    title: "Media Library",
    description: "Browse your entire media collection. Filter by movies, TV shows, or music.",
    route: "/media",
  },
  {
    id: "search",
    title: "Smart Search",
    description: "Use AI-powered semantic search to find content. Try voice search too!",
    route: "/search",
  },
  {
    id: "plugins",
    title: "Plugin Marketplace",
    description: "Extend functionality with community plugins for themes, integrations, and more.",
    route: "/plugins",
  },
  {
    id: "complete",
    title: "You're All Set!",
    description: "Start exploring your media center. You can restart this tour anytime from Settings.",
    route: "/dashboard",
  },
];

export function OnboardingTour() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasSavedProgress, setHasSavedProgress] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [errorHistory, setErrorHistory] = useState<Array<{ step: number; error: string; timestamp: number }>>([]);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const location = useLocation();
  const navigate = useNavigate();

  const { progress: savedProgress, isLoading: isLoadingProgress, isSyncing, saveProgress, clearProgress } = useTourProgress(userId);
  const { trackAnalyticsEvent } = useTourAnalytics(userId);

  // Get current user
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id);
    };
    getUserId();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUserId(session?.user?.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Helper function to track tour events
  const trackTourEvent = (eventName: string, properties?: Record<string, any>) => {
    trackEvent(`tour_${eventName}`, {
      step_id: tourSteps[currentStep]?.id,
      step_number: currentStep + 1,
      total_steps: tourSteps.length,
      user_authenticated: !!userId,
      ...properties,
    });
  };

  // Initialize tour from saved progress
  useEffect(() => {
    if (isLoadingProgress) return;

    // Check debug mode from localStorage or URL params
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('tourDebug');
    const debugStorage = localStorage.getItem('tourDebugMode');
    setDebugMode(debugParam === 'true' || debugStorage === 'true');

    // Load progress from backend/localStorage
    if (savedProgress) {
      if (savedProgress.completed) {
        // Tour is completed
        setIsOpen(false);
        setHasSavedProgress(false);
      } else if (savedProgress.paused) {
        // Tour is paused - show resume option
        setHasSavedProgress(true);
        setCurrentStep(savedProgress.current_step);
        setErrorHistory(savedProgress.error_history || []);
      }
    } else if (location.pathname === "/dashboard") {
      // Start tour immediately on first dashboard visit
      setTimeout(() => {
        setIsOpen(true);
        trackTourEvent('started');
        trackAnalyticsEvent({
          event_type: 'started',
          step_number: 0,
          step_id: tourSteps[0].id,
        });
      }, 500);
    }
  }, [location.pathname, savedProgress, isLoadingProgress, trackAnalyticsEvent]);

  // Save progress whenever step changes
  useEffect(() => {
    if (isOpen && currentStep > 0 && !isLoadingProgress) {
      saveProgress({
        current_step: currentStep,
        completed: false,
        paused: false,
        error_history: errorHistory,
      });
    }
  }, [currentStep, isOpen, isLoadingProgress, errorHistory, saveProgress]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          handleSkip();
          break;
        case "ArrowRight":
          e.preventDefault();
          handleNext();
          break;
        case "ArrowLeft":
          e.preventDefault();
          handlePrevious();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentStep]);

  // Listen for restart and auto-recover events
  useEffect(() => {
    const handleRestart = () => {
      setCurrentStep(0);
      setIsOpen(true);
      trackTourEvent('restarted');
    };

    const handleAutoRecoverEvent = () => {
      handleAutoRecover();
    };

    window.addEventListener("restart-onboarding", handleRestart);
    window.addEventListener("tour-auto-recover", handleAutoRecoverEvent);
    return () => {
      window.removeEventListener("restart-onboarding", handleRestart);
      window.removeEventListener("tour-auto-recover", handleAutoRecoverEvent);
    };
  }, [currentStep, errorHistory]);

  const handleNext = () => {
    if (isTransitioning) return;
    
    if (currentStep < tourSteps.length - 1) {
      setIsTransitioning(true);
      trackTourEvent('next_clicked', { from_step: currentStep });
      
      setTimeout(() => {
        try {
          const nextStep = currentStep + 1;
          setCurrentStep(nextStep);
          const nextRoute = tourSteps[nextStep].route;
          if (nextRoute && nextRoute !== location.pathname) {
            navigate(nextRoute);
          }
          trackTourEvent('step_viewed', { step: nextStep });
          trackAnalyticsEvent({
            event_type: 'step_viewed',
            step_number: nextStep,
            step_id: tourSteps[nextStep].id,
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          console.error("Navigation error in tour:", error);
          
          // Track error
          trackTourEvent('error', {
            error_message: errorMessage,
            error_type: 'navigation',
            attempted_step: currentStep + 1,
          });
          
          trackAnalyticsEvent({
            event_type: 'error',
            step_number: currentStep,
            step_id: tourSteps[currentStep].id,
            error_message: errorMessage,
          });
          
          // Add to error history
          setErrorHistory(prev => [...prev, {
            step: currentStep,
            error: errorMessage,
            timestamp: Date.now(),
          }]);
          
          throw error; // Let error boundary catch it
        } finally {
          setIsTransitioning(false);
        }
      }, 150);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (isTransitioning || currentStep === 0) return;
    
    setIsTransitioning(true);
    trackTourEvent('previous_clicked', { from_step: currentStep });
    
    setTimeout(() => {
      try {
        const prevStep = currentStep - 1;
        setCurrentStep(prevStep);
        const prevRoute = tourSteps[prevStep].route;
        if (prevRoute && prevRoute !== location.pathname) {
          navigate(prevRoute);
        }
        trackTourEvent('step_viewed', { step: prevStep });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error("Navigation error in tour:", error);
        
        // Track error
        trackTourEvent('error', {
          error_message: errorMessage,
          error_type: 'navigation',
          attempted_step: currentStep - 1,
        });
        
        // Add to error history
        setErrorHistory(prev => [...prev, {
          step: currentStep,
          error: errorMessage,
          timestamp: Date.now(),
        }]);
        
        throw error; // Let error boundary catch it
      } finally {
        setIsTransitioning(false);
      }
    }, 150);
  };

  const handleComplete = () => {
    trackTourEvent('completed', { 
      total_errors: errorHistory.length,
      completion_time: Date.now() 
    });
    trackAnalyticsEvent({
      event_type: 'completed',
      step_number: currentStep,
      step_id: tourSteps[currentStep].id,
    });
    saveProgress({
      current_step: currentStep,
      completed: true,
      paused: false,
      error_history: errorHistory,
    });
    setIsOpen(false);
    setCurrentStep(0);
    setHasSavedProgress(false);
  };

  const handleSkip = () => {
    trackTourEvent('skipped', { 
      at_step: currentStep,
      total_errors: errorHistory.length 
    });
    trackAnalyticsEvent({
      event_type: 'skipped',
      step_number: currentStep,
      step_id: tourSteps[currentStep].id,
    });
    saveProgress({
      current_step: currentStep,
      completed: true,
      paused: false,
      error_history: errorHistory,
    });
    setIsOpen(false);
    setCurrentStep(0);
    setHasSavedProgress(false);
  };

  const handlePause = () => {
    trackTourEvent('paused', { at_step: currentStep });
    trackAnalyticsEvent({
      event_type: 'paused',
      step_number: currentStep,
      step_id: tourSteps[currentStep].id,
    });
    saveProgress({
      current_step: currentStep,
      completed: false,
      paused: true,
      error_history: errorHistory,
    });
    setIsOpen(false);
    setHasSavedProgress(true);
  };

  const handleResume = () => {
    try {
      if (savedProgress) {
        const step = savedProgress.current_step;
        setCurrentStep(step);
        setErrorHistory(savedProgress.error_history || []);
        const route = tourSteps[step].route;
        if (route && route !== location.pathname) {
          navigate(route);
        }
        trackTourEvent('resumed', { from_step: step });
      }
      setIsOpen(true);
      setHasSavedProgress(false);
      saveProgress({
        current_step: savedProgress?.current_step || 0,
        completed: false,
        paused: false,
        error_history: errorHistory,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error("Error resuming tour:", error);
      
      trackTourEvent('error', {
        error_message: errorMessage,
        error_type: 'resume',
      });
      
      // Clear corrupted data and start fresh
      clearProgress();
      setCurrentStep(0);
      setIsOpen(true);
      setHasSavedProgress(false);
    }
  };

  // Auto-recovery: Skip problematic step and continue tour
  const handleAutoRecover = () => {
    trackTourEvent('auto_recovery', { 
      failed_step: currentStep,
      error_count: errorHistory.filter(e => e.step === currentStep).length 
    });
    
    // Try to skip to next step
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const nextRoute = tourSteps[nextStep].route;
      
      try {
        if (nextRoute && nextRoute !== location.pathname) {
          navigate(nextRoute);
        }
        setIsOpen(true);
      } catch (error) {
        console.error("Auto-recovery navigation failed:", error);
        // If recovery fails, just complete the tour
        handleComplete();
      }
    } else {
      // If it's the last step, just complete
      handleComplete();
    }
  };

  // Show loading state while fetching progress
  if (isLoadingProgress) {
    return null;
  }

  // Show resume button if tour is closed but has saved progress
  if (!isOpen && hasSavedProgress) {
    return (
      <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-300">
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div>
                <h3 className="font-semibold">Resume Your Tour?</h3>
                <p className="text-sm text-muted-foreground">
                  Pick up where you left off (Step {currentStep + 1} of {tourSteps.length})
                  {userId && isSyncing && (
                    <span className="ml-2 inline-flex items-center">
                      <Loader2 className="h-3 w-3 animate-spin" />
                    </span>
                  )}
                </p>
                {userId && (
                  <p className="text-xs text-muted-foreground mt-1">
                    ✓ Synced across devices
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleResume} size="sm" disabled={isSyncing}>
                  Resume Tour
                </Button>
                <Button 
                  onClick={() => {
                    clearProgress();
                    setHasSavedProgress(false);
                  }} 
                  variant="ghost" 
                  size="sm"
                  disabled={isSyncing}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isOpen) return null;

  const step = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <>
      {/* Overlay - clickable to close */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100]" 
        onClick={handleSkip}
      />

      {/* Debug Mode Toggle */}
      {debugMode && (
        <div className="fixed top-4 right-4 z-[102] pointer-events-auto">
          <Card className="shadow-lg border-yellow-500/50 bg-yellow-500/10">
            <CardContent className="pt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Bug className="h-4 w-4" />
                Debug Mode
              </div>
              <div className="text-xs space-y-1 text-muted-foreground">
                <div>Step: {currentStep + 1}/{tourSteps.length}</div>
                <div>ID: {tourSteps[currentStep]?.id}</div>
                <div>Route: {tourSteps[currentStep]?.route || 'none'}</div>
                <div>Errors: {errorHistory.length}</div>
                {errorHistory.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">Error History</summary>
                    <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                      {errorHistory.map((err, idx) => (
                        <div key={idx} className="text-[10px] p-1 bg-destructive/10 rounded">
                          <div>Step {err.step + 1}: {err.error}</div>
                          <div className="opacity-70">{new Date(err.timestamp).toLocaleTimeString()}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => {
                  localStorage.setItem('tourDebugMode', 'false');
                  setDebugMode(false);
                }}
                className="w-full mt-2"
              >
                Disable Debug
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tour Card */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-lg">
          <Card 
            key={currentStep} 
            className="shadow-2xl animate-in fade-in zoom-in-95 duration-300"
          >
          <CardHeader>
            <div className="flex items-start justify-between">
              <div 
                key={`content-${currentStep}`}
                className={cn(
                  "space-y-1 transition-opacity duration-200",
                  isTransitioning ? "opacity-0" : "opacity-100"
                )}
              >
                <CardTitle className="text-2xl">{step.title}</CardTitle>
                <CardDescription className="text-base">{step.description}</CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSkip}
                className="shrink-0"
                aria-label="Skip tour"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {/* Progress Bar */}
            <div 
              className={cn(
                "space-y-2 transition-opacity duration-200",
                isTransitioning ? "opacity-0" : "opacity-100"
              )}
            >
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Step {currentStep + 1} of {tourSteps.length}
                </span>
                <span className="text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Step Indicators */}
            <div className="flex gap-2 mt-6 justify-center">
              {tourSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={cn(
                    "h-2 rounded-full transition-all",
                    index === currentStep
                      ? "w-8 bg-primary"
                      : index < currentStep
                      ? "w-2 bg-primary/50"
                      : "w-2 bg-secondary"
                  )}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>

            <div className="flex gap-2">
              {currentStep < tourSteps.length - 1 && (
                <>
                  <Button variant="ghost" onClick={handlePause}>
                    Pause Tour
                  </Button>
                  <Button variant="ghost" onClick={handleSkip}>
                    Skip Tour
                  </Button>
                </>
              )}
              <Button onClick={handleNext}>
                {currentStep === tourSteps.length - 1 ? (
                  "Get Started"
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
        </div>
      </div>
    </>
  );
}

// Export function to restart tour manually
export function restartOnboardingTour() {
  // Clear both localStorage and backend
  localStorage.removeItem("hasSeenOnboardingTour");
  localStorage.removeItem("onboardingTourStep");
  
  // Trigger restart event
  window.dispatchEvent(new CustomEvent("restart-onboarding"));
  
  // Clear backend progress if user is authenticated
  supabase.auth.getUser().then(({ data: { user } }) => {
    if (user) {
      supabase
        .from('tour_progress')
        .delete()
        .eq('user_id', user.id)
        .then(() => {
          trackEvent('tour_manually_restarted', { user_id: user.id });
        });
    }
  });
}
